import { useState, useEffect, useCallback } from 'react';
import PartyMode from '../Party/PartyMode';
import '../Party/PartyMode.css';
import {
  DEFAULT_CLIENT_ID,
  initiateAuth,
  exchangeCode,
  getRedirectUri,
  loadStoredToken,
  loadStoredUser,
  storeUser,
  clearStoredToken,
  getValidToken,
  getMe,
} from './spotify.js';
import './Strobe.css';

export default function Strobe({ postAuthPath = '/party', demoMode = false }) {
  const [spotifyClientId, setSpotifyClientId] = useState(
    () => localStorage.getItem('sp_client_id') || DEFAULT_CLIENT_ID
  );
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyUsername, setSpotifyUsername] = useState('');
  const [spotifyError, setSpotifyError] = useState('');
  const [spotifyAuthLoading, setSpotifyAuthLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const oauthError = params.get('error');
    const authState = params.get('state');
    const callbackUrl = sessionStorage.getItem('sp_return_path') || postAuthPath;

    if (oauthError) {
      window.history.replaceState({}, '', callbackUrl);
      setSpotifyError('Spotify authorization was denied.');
      return;
    }

    if (code) {
      window.history.replaceState({}, '', callbackUrl);
      const cid = sessionStorage.getItem('sp_client_id')
        || localStorage.getItem('sp_client_id')
        || DEFAULT_CLIENT_ID;
      if (!cid) {
        setSpotifyError('Could not find Client ID — please try connecting again.');
        return;
      }
      setSpotifyAuthLoading(true);
      exchangeCode(code, cid, authState)
        .then(async (token) => {
          setSpotifyConnected(true);
          setSpotifyClientId(cid);
          sessionStorage.removeItem('sp_return_path');
          const me = await getMe(token);
          if (me) {
            storeUser(me);
            if (me.display_name || me.id) setSpotifyUsername(me.display_name || me.id);
          }
        })
        .catch((err) => setSpotifyError(err.message))
        .finally(() => setSpotifyAuthLoading(false));
    }
  }, [postAuthPath]);

  useEffect(() => {
    const cid = localStorage.getItem('sp_client_id') || DEFAULT_CLIENT_ID;
    const stored = loadStoredToken();
    if (!cid || !stored) return;
    const storedUser = loadStoredUser();
    if (storedUser?.display_name) setSpotifyUsername(storedUser.display_name);
    setSpotifyClientId(cid);
    getValidToken(cid).then(async (token) => {
      if (token) {
        setSpotifyConnected(true);
        const me = await getMe(token);
        if (me) {
          storeUser(me);
          if (me.display_name || me.id) setSpotifyUsername(me.display_name || me.id);
        }
      } else {
        clearStoredToken();
      }
    });
  }, []);

  const handleConnect = useCallback(() => {
    const clientId = spotifyClientId.trim() || DEFAULT_CLIENT_ID;
    if (!clientId) {
      setSpotifyError('Please enter your Client ID first.');
      return;
    }
    setSpotifyError('');
    localStorage.setItem('sp_client_id', clientId);
    initiateAuth(clientId, postAuthPath);
  }, [spotifyClientId, postAuthPath]);

  const handleDisconnect = useCallback(() => {
    clearStoredToken();
    localStorage.removeItem('sp_client_id');
    setSpotifyConnected(false);
    setSpotifyUsername('');
    setSpotifyClientId(DEFAULT_CLIENT_ID);
  }, []);

  return (
    <div className="app-root">
      <PartyMode
        clientId={spotifyClientId.trim() || DEFAULT_CLIENT_ID}
        spotifyConnected={spotifyConnected}
        spotifyUsername={spotifyUsername}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        authLoading={spotifyAuthLoading}
        authError={spotifyError}
        redirectUri={getRedirectUri()}
        hasDefaultClientId={Boolean(DEFAULT_CLIENT_ID)}
        clientIdInput={spotifyClientId}
        onClientIdChange={setSpotifyClientId}
        demoMode={demoMode}
      />
    </div>
  );
}
